<template>
  <div class="content-factory">
    <!-- ============ 卡片一：AI 文案生成（洗稿 / 代写） ============ -->
    <el-card shadow="never">
      <template #header>
        <div class="card-header">
          <span>✍️ AI 文案生成 · 五件套</span>
          <el-tag type="success" size="small" v-if="taskId">任务 #{{ taskId }}：{{ taskStatusText }}</el-tag>
        </div>
      </template>

      <el-form label-width="90px" label-position="top">
        <el-form-item label="生成方式">
          <el-radio-group v-model="form.mode">
            <el-radio-button value="rewrite">① 洗稿（同行文案→改写）</el-radio-button>
            <el-radio-button value="generate">② AI 代写（从零创作）</el-radio-button>
          </el-radio-group>
        </el-form-item>

        <el-row :gutter="16">
          <el-col :xs="24" :sm="8">
            <el-form-item label="行业（内容风格）">
              <el-select v-model="form.industry" placeholder="选行业" clearable filterable style="width:100%">
                <el-option v-for="ind in industryNames" :key="ind" :label="ind" :value="ind" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :xs="24" :sm="8">
            <el-form-item label="品牌/账号名">
              <el-input v-model="form.brand" placeholder="如：A咖果派" />
            </el-form-item>
          </el-col>
          <el-col :xs="24" :sm="8">
            <el-form-item label="发布平台">
              <el-select v-model="form.platform" style="width:100%">
                <el-option label="抖音" value="douyin" />
                <el-option label="快手" value="kuaishou" />
                <el-option label="小红书" value="xhs" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>

        <!-- 洗稿模式 -->
        <template v-if="form.mode === 'rewrite'">
          <el-form-item label="粘贴同行文案（直接洗稿）">
            <el-input
              v-model="form.source_text"
              type="textarea"
              :rows="5"
              placeholder="粘贴同行视频文案/口播稿，AI 会深度洗稿后生成五件套…"
            />
          </el-form-item>
          <el-divider content-position="center">或</el-divider>
          <el-form-item label="上传同行视频/音频（whisper 提取口播）">
            <!-- 未选择：上传按钮 -->
            <div v-if="!fileName" class="upload-row">
              <el-upload
                :auto-upload="false"
                :limit="1"
                :on-change="onFileChange"
                accept="video/*,audio/*"
              >
                <el-button>📁 选择视频/音频文件</el-button>
              </el-upload>
              <span class="file-name">未选择文件（可选）</span>
            </div>
            <!-- 🧠 批次 25：已上传后紧凑文件条（不再显示大文件框） -->
            <div v-else class="file-compact">
              <div class="thumb-wrap" @click="peerPreviewSrc && (peerPreviewFullscreen = true)" title="点击放大预览">
                <img v-if="!peerPreviewIsVideo && peerPreviewSrc" :src="peerPreviewSrc" class="thumb" />
                <video v-else-if="peerPreviewIsVideo && peerPreviewSrc" :src="peerPreviewSrc" muted playsinline class="thumb"></video>
                <div v-if="peerPreviewSrc" class="thumb-play">▶</div>
                <div v-else class="thumb-empty">🎬</div>
              </div>
              <div class="file-info">
                <div class="file-status">✅ 已上传成功</div>
                <div class="file-name-sm" :title="fileName">{{ fileName }}</div>
              </div>
              <div class="file-actions">
                <el-button size="mini" @click="peerPreviewFullscreen = true" :disabled="!peerPreviewSrc" icon="el-icon-video-play">播放</el-button>
                <el-button size="mini" @click="peerPreviewFullscreen = true" :disabled="!peerPreviewSrc" icon="el-icon-zoom-in">放大</el-button>
                <el-button size="mini" @click="$refs.peerFileRef && $refs.peerFileRef.click()" icon="el-icon-refresh">换</el-button>
                <el-button size="mini" type="danger" @click="clearPeerFile" icon="el-icon-delete">删除</el-button>
              </div>
            </div>
            <!-- 隐藏文件输入（「换」按钮触发重新选文件） -->
            <input ref="peerFileRef" type="file" accept="video/*,audio/*" style="display:none" @change="onPeerFileRef" />
            <!-- 🧠 批次 23：选择同行视频后自动识别口播 → 回填到「粘贴同行文案」文本框 -->
            <div v-if="peerAsrLoading" class="asr-progress asr-progress-run" style="margin-top:8px">
              <div class="asr-pg-row">
                <div class="asr-pg-spin"></div>
                <div class="asr-pg-main">
                  <span class="sp">🧠 AI 正在听同行视频口播 · ⏱ {{ peerAsrElapsed }}s</span>
                  <span class="stg">{{ peerAsrStage || '正在准备…' }}</span>
                </div>
              </div>
              <div class="asr-pg-bar"><div class="asr-pg-bar-inner" :style="{ width: (peerAsrProgress * 100).toFixed(1) + '%' }"></div></div>
            </div>
          </el-form-item>
        </template>

        <!-- AI 代写模式 -->
        <template v-else>
          <el-form-item label="产品/服务信息（卖点、优势，越具体越好）">
            <el-input
              v-model="form.product_info"
              type="textarea"
              :rows="4"
              placeholder="例：便携咖啡机，新款上市，全国包邮，支持刻字定制，质保一年…"
            />
          </el-form-item>
        </template>

        <el-form-item>
          <el-button type="primary" size="large" :loading="submitting" @click="submit">
            🚀 {{ form.mode === 'generate' ? 'AI 代写五件套' : '生成五件套' }}
          </el-button>
          <el-button v-if="taskId && taskStatus !== 'done'" size="large" @click="pollTask">刷新状态</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- ============ 卡片二：上传自有素材快速发布 ============ -->
    <el-card shadow="never" class="mt-16">
      <template #header>
        <div class="card-header">
          <span>📤 上传自有素材快速发布（自己拍的视频/图片 + 自己的文案）</span>
          <span v-if="cfSavedAt" class="save-tip">💾 已自动保存 {{ cfSavedAt }}</span>
        </div>
      </template>
      <el-form label-width="90px" label-position="top">
        <el-row :gutter="16">
          <el-col :xs="24" :sm="8">
            <el-form-item label="素材文件（视频/图片）">
              <!-- 未选择：上传按钮 -->
              <div v-if="!ownFileName">
                <el-upload
                  :auto-upload="false"
                  :limit="1"
                  :on-change="onOwnFileChange"
                  accept="video/*,image/*"
                >
                  <el-button>📁 选择素材</el-button>
                </el-upload>
                <span class="file-name">未选择文件</span>
              </div>
              <!-- 🧠 批次 25：已上传后紧凑文件条（不再显示大文件框） -->
              <div v-else class="file-compact">
                <div class="thumb-wrap" @click="ownPreviewSrc && (ownPreviewFullscreen = true)" title="点击放大预览">
                  <img v-if="!ownPreviewIsVideo && ownPreviewSrc" :src="ownPreviewSrc" class="thumb" />
                  <video v-else-if="ownPreviewIsVideo && ownPreviewSrc" :src="ownPreviewSrc" muted playsinline class="thumb"></video>
                  <div v-if="ownPreviewSrc" class="thumb-play">▶</div>
                  <div v-else class="thumb-empty">🎬</div>
                </div>
                <div class="file-info">
                  <div class="file-status">✅ 已上传成功</div>
                  <div class="file-name-sm" :title="ownFileName">{{ ownFileName }}</div>
                </div>
                <div class="file-actions">
                  <el-button size="mini" @click="ownPreviewFullscreen = true" :disabled="!ownPreviewSrc" icon="el-icon-video-play">播放</el-button>
                  <el-button size="mini" @click="ownPreviewFullscreen = true" :disabled="!ownPreviewSrc" icon="el-icon-zoom-in">放大</el-button>
                  <el-button size="mini" @click="$refs.ownFileRef && $refs.ownFileRef.click()" icon="el-icon-refresh">换</el-button>
                  <el-button size="mini" type="danger" @click="clearOwnFile" icon="el-icon-delete">删除</el-button>
                </div>
              </div>
              <!-- 隐藏文件输入（「换」按钮触发重新选文件） -->
              <input ref="ownFileRef" type="file" accept="video/*,image/*" style="display:none" @change="onOwnFileRef" />
            </el-form-item>
          </el-col>
          <el-col :xs="24" :sm="8">
            <el-form-item label="平台">
              <el-select v-model="ownForm.platform" style="width:100%">
                <el-option label="抖音" value="douyin" />
                <el-option label="快手" value="kuaishou" />
                <el-option label="小红书" value="xhs" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :xs="24" :sm="8">
            <el-form-item label="话题标签">
              <div style="display:flex;gap:6px">
                <el-input v-model="ownForm.tags" placeholder="如：家居好物,新店开业" />
                <el-button size="small" class="ai-rewrite-btn" :loading="_rwBusy==='ownForm.tags'"
                  @click="aiRewrite('ownForm.tags', ownForm.tags, 'tags')" title="AI 智能重写话题">✨ AI</el-button>
              </div>
            </el-form-item>
          </el-col>
        </el-row>
        <!-- 🧠 2026-09-07：视频语音自动识别 → 转写原文 + 五件套（批次 19：改蓝色系、紧凑不压版面） -->
        <div v-if="ownAsrLoading" class="asr-progress asr-progress-run">
          <div class="asr-pg-row">
            <div class="asr-pg-spin"></div>
            <div class="asr-pg-main">
              <span class="sp">🧠 AI 正在听视频语音并写文案 · ⏱ {{ ownAsrElapsed }}s</span>
              <span class="stg">{{ ownAsrStage || '正在准备…' }}</span>
            </div>
          </div>
          <div class="asr-pg-bar"><div class="asr-pg-bar-inner" :style="{ width: (ownAsrProgress * 100).toFixed(1) + '%' }"></div></div>
        </div>

        <el-form-item label="标题">
          <div style="display:flex;gap:6px">
            <el-input v-model="ownForm.title" placeholder="视频/笔记标题（识别完成会自动填好）" :maxlength="50" show-word-limit />
            <el-button size="small" class="ai-rewrite-btn" :loading="_rwBusy==='ownForm.title'"
              @click="aiRewrite('ownForm.title', ownForm.title, 'title')" title="AI 智能重写标题">✨ AI</el-button>
          </div>
        </el-form-item>
        <el-form-item label="文案描述">
          <div style="display:flex;gap:6px">
            <el-input
              v-model="ownForm.desc"
              type="textarea"
              :rows="4"
              placeholder="自己写的文案，或视频识别后 AI 自动填入"
              style="flex:1"
            />
            <el-button size="small" class="ai-rewrite-btn" :loading="_rwBusy==='ownForm.desc'"
              @click="aiRewrite('ownForm.desc', ownForm.desc, 'desc')" title="AI 智能重写描述">✨ AI</el-button>
          </div>
        </el-form-item>

        <!-- 🧠 识别结果：语音转文字 + 五件套（默认抖音） -->
        <template v-if="ownAsr">
          <el-divider class="asr-divider">🧠 AI 视频识别结果
            <span class="asr-meta">语音转文字 {{ ownAsr.asr_seconds || 0 }}s · 共 {{ ownAsr.transcript_len || 0 }} 字</span>
          </el-divider>
          <el-alert v-if="ownAsr.warn" type="warning" :closable="false" :title="ownAsr.warn" style="margin-bottom:12px" />
          <!-- 批次 21：操作栏移进识别区，五件套自成一体（回填/复制/重新识别） -->
          <div class="asr-ops">
            <el-button size="small" @click="fillOwnFromAsr">📥 回填标题+描述到上方表单</el-button>
            <el-button size="small" @click="copyOwnAsrAll">📋 一键复制五件套</el-button>
            <el-button size="small" type="warning" :loading="fiveRegenLoading" @click="regenerateFive">🎨 一键重生 5 件套</el-button>
            <el-button size="small" :loading="ownAsrLoading" @click="runOwnAsr">🔄 重新识别</el-button>
          </div>
          <el-form-item label="🎙 视频语音转文字（识别原文，可直接修改）">
            <el-input v-model="ownAsr.transcript" type="textarea" :rows="5" />
          </el-form-item>
          <el-row :gutter="12">
            <el-col :xs="24" :sm="12">
              <el-form-item label="① 抖音标题（已同步到上方标题）">
                <div style="display:flex;gap:6px">
                  <el-input v-model="ownAsr.five.title" maxlength="45" show-word-limit style="flex:1" />
                  <el-button size="small" class="ai-rewrite-btn" :loading="_rwBusy==='ownAsr.five.title'"
                    @click="aiRewrite('ownAsr.five.title', ownAsr.five.title, 'title')" title="AI 智能重写标题">✨ AI</el-button>
                </div>
              </el-form-item>
            </el-col>
            <el-col :xs="24" :sm="12">
              <el-form-item label="② 抖音描述（已同步到上方描述）">
                <div style="display:flex;gap:6px">
                  <el-input v-model="ownAsr.five.desc" type="textarea" :rows="4" style="flex:1" />
                  <el-button size="small" class="ai-rewrite-btn" :loading="_rwBusy==='ownAsr.five.desc'"
                    @click="aiRewrite('ownAsr.five.desc', ownAsr.five.desc, 'desc')" title="AI 智能重写描述">✨ AI</el-button>
                </div>
              </el-form-item>
            </el-col>
            <el-col :xs="24" :sm="12">
              <el-form-item label="③ 评论区互动话术">
                <div style="display:flex;gap:6px">
                  <el-input v-model="ownAsr.five.comment" type="textarea" :rows="3" style="flex:1" />
                  <el-button size="small" class="ai-rewrite-btn" :loading="_rwBusy==='ownAsr.five.comment'"
                    @click="aiRewrite('ownAsr.five.comment', ownAsr.five.comment, 'comment')" title="AI 智能重写评论">✨ AI</el-button>
                </div>
              </el-form-item>
            </el-col>
            <el-col :xs="24" :sm="12">
              <el-form-item label="④ 朋友圈文案">
                <div style="display:flex;gap:6px">
                  <el-input v-model="ownAsr.five.moments" type="textarea" :rows="3" style="flex:1" />
                  <el-button size="small" class="ai-rewrite-btn" :loading="_rwBusy==='ownAsr.five.moments'"
                    @click="aiRewrite('ownAsr.five.moments', ownAsr.five.moments, 'moments')" title="AI 智能重写朋友圈">✨ AI</el-button>
                </div>
              </el-form-item>
            </el-col>
            <el-col :span="24">
              <el-form-item label="⑤ 小红书笔记">
                <div style="display:flex;gap:6px">
                  <el-input v-model="ownAsr.five.xhs" type="textarea" :rows="4" style="flex:1" />
                  <el-button size="small" class="ai-rewrite-btn" :loading="_rwBusy==='ownAsr.five.xhs'"
                    @click="aiRewrite('ownAsr.five.xhs', ownAsr.five.xhs, 'xhs')" title="AI 智能重写小红书">✨ AI</el-button>
                </div>
              </el-form-item>
            </el-col>
          </el-row>
        </template>

        <el-form-item>
          <el-button type="success" size="large" :loading="ownSubmitting" @click="submitOwn">
            📥 直接入发布队列
          </el-button>
          <el-button type="primary" size="large" :loading="ownSubmitting" @click="publishNow">
            🚀 立即发布
          </el-button>
          <span class="hint">{{ ownAsr ? '（素材已上传，直接入队不会重复上传；五件套在上方编辑；✨AI 按钮可智能重写文本框）' : '（跳过 AI 生成，素材+文案直接进入发布队列；✨AI 按钮可智能重写文本框；无素材可纯文案入队）' }}</span>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- ============ 五件套结果 ============ -->
    <el-card v-if="five" shadow="never" class="mt-16">
      <template #header>
        <div class="card-header">
          <span>📦 五件套成品</span>
          <div>
            <el-button size="small" @click="copyAll">📋 复制全部</el-button>
            <el-button size="small" type="primary" @click="goPublish">→ 去发布队列</el-button>
          </div>
        </div>
      </template>
      <el-alert
        v-if="bannedHits.length"
        type="error" :closable="false" show-icon
        :title="'⚠️ 检测到违禁词：' + bannedHits.join('、') + '（请修改后再发布）'"
        style="margin-bottom:12px"
      />
      <el-row :gutter="12">
        <el-col :xs="24" :sm="12" v-for="item in fiveList" :key="item.key" style="margin-bottom:12px">
          <div class="five-item">
            <div class="five-label">{{ item.label }}</div>
            <el-input
              v-model="item.value"
              type="textarea"
              :rows="item.rows"
              :maxlength="item.max"
              show-word-limit
            />
          </div>
        </el-col>
      </el-row>
      <div style="text-align:right;margin-top:8px">
        <el-button size="small" @click="fillOwnFromFive">📥 填入「快速发布」表单（默认抖音）</el-button>
        <el-button type="primary" size="small" @click="publishFive">📥 五件套全部入发布队列</el-button>
      </div>
    </el-card>

    <!-- 🧠 批次 24：同行视频/音频预览放大弹窗 -->
    <div v-if="peerPreviewSrc" v-show="peerPreviewFullscreen" class="preview-modal" @click.self="peerPreviewFullscreen = false">
      <div class="preview-modal-inner">
        <video v-if="peerPreviewIsVideo" :src="peerPreviewSrc" controls autoplay playsinline class="preview-modal-media"></video>
        <img v-else :src="peerPreviewSrc" class="preview-modal-media" />
        <div class="preview-modal-actions">
          <el-button type="primary" @click="peerPreviewFullscreen = false">✕ 关闭预览</el-button>
        </div>
      </div>
    </div>
    <!-- 🧠 批次 24：自有素材预览放大弹窗 -->
    <div v-if="ownPreviewSrc" v-show="ownPreviewFullscreen" class="preview-modal" @click.self="ownPreviewFullscreen = false">
      <div class="preview-modal-inner">
        <video v-if="ownPreviewIsVideo" :src="ownPreviewSrc" controls autoplay playsinline class="preview-modal-media"></video>
        <img v-else :src="ownPreviewSrc" class="preview-modal-media" />
        <div class="preview-modal-actions">
          <el-button type="primary" @click="ownPreviewFullscreen = false">✕ 关闭预览</el-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '../api'
import { uploadChunked } from '../utils/chunkUpload'

export default {
  name: 'ContentFactory',
  data() {
    return {
      form: {
        account_id: 0,
        mode: 'rewrite',
        source_text: '',
        product_info: '',
        industry: '',
        brand: '',
        platform: 'douyin',
        asset_id: 0
      },
      industryNames: [],
      fileName: '',
      // 🧠 批次 24：同行视频本地预览 + 放大
      peerPreviewSrc: '',
      peerPreviewIsVideo: false,
      peerPreviewFullscreen: false,
      submitting: false,
      taskId: null,
      taskStatus: '',
      five: null,
      fiveList: [],
      bannedHits: [],
      // 自有素材发布
      ownForm: { account_id: 0, platform: 'douyin', tags: '', title: '', desc: '' },
      ownFileName: '',
      // 🧠 批次 24：自有素材本地预览 + 放大
      ownPreviewSrc: '',
      ownPreviewIsVideo: false,
      ownPreviewFullscreen: false,
      ownSubmitting: false,
      // 🧠 2026-09-06：上传视频自动识别（语音转文字 + 五件套）
      ownAsrLoading: false,
      // 🧠 批次 18：异步识别进度（后端任务化 + 前端轮询）
      ownAsrStage: '',
      ownAsrProgress: 0,
      ownAsrElapsed: 0,
      ownAsr: null,          // { transcript, transcript_len, asr_seconds, warn, five }
      // 🧠 批次 23：同行视频自动识别口播 → 回填 source_text
      peerAsrLoading: false,
      peerAsrStage: '',
      peerAsrProgress: 0,
      peerAsrElapsed: 0,
      ownAsset: null,        // 识别时上传的素材 { id, file_path, type }，入队复用
      peerAsset: null,       // 🧠 批次 24：同行视频识别时上传的素材，重新识别复用不重新上传
      cfSavedAt: '',         // 🧠 批次 22：自动保存时间戳
      _rwBusy: null,         // 🧠 批次 25：AI 重写中的字段（用于显示 loading）
      fiveRegenLoading: false  // 🧠 批次 25：一键重生 5 件套加载中
    }
  },
  // 🧠 批次 22：文案自动保存到 localStorage —— 切菜单不丢五件套，可直接复制
  //    用户反馈：「复制要来回切换菜单键，不能切换菜单就自动没有文本可以复制了」
  watch: {
    ownForm: { deep: true, handler() { this._saveCfDraft() } },
    ownAsr: { deep: true, handler() { this._saveCfDraft() } }
  },
  computed: {
    taskStatusText() {
      return { pending: '排队中', running: '生成中…', done: '完成 ✅', failed: '失败 ❌' }[this.taskStatus] || this.taskStatus
    }
  },
  created() {
    this.loadIndustries()
    const acc = localStorage.getItem('current_account_id')
    if (acc) {
      this.form.account_id = Number(acc)
      this.ownForm.account_id = Number(acc)
    }
    this._restoreCfDraft()
  },
  beforeDestroy() {
    if (this._cfSaveTimer) { clearTimeout(this._cfSaveTimer); this._cfSaveTimer = null }
    this._saveCfDraftNow()      // 离开页面前把最后 500ms 内的修改也落盘
    // 🧠 批次 24：释放本地预览的 Blob URL，避免内存泄漏
    if (this._peerPreviewUrl) { URL.revokeObjectURL(this._peerPreviewUrl); this._peerPreviewUrl = null }
    if (this._ownPreviewUrl) { URL.revokeObjectURL(this._ownPreviewUrl); this._ownPreviewUrl = null }
  },
  methods: {
    async loadIndustries() {
      try {
        const r = await api.get('/industries/')
        this.industryNames = r.names || []
      } catch (e) { console.error(e) }
    },
    onFileChange(file) {
      this.fileName = file.name
      this._file = file.raw
      this.peerAsset = null     // 换素材后缓存的上传结果失效
      // 🧠 批次 24：本地预览（不上传服务器，纯浏览器播放）
      if (this._peerPreviewUrl) URL.revokeObjectURL(this._peerPreviewUrl)
      this._peerPreviewUrl = URL.createObjectURL(file.raw)
      this.peerPreviewSrc = this._peerPreviewUrl
      this.peerPreviewIsVideo = (file.raw.type || '').startsWith('video/')
      // 🧠 批次 23：上传同行视频/音频后自动开始识别 → 口播回填「粘贴同行文案」文本框
      //    （此前只有「自有素材」区自动识别；同行视频要用户手动选文件后再点「生成五件套」，
      //     中间没有识别环节，用户看不到提取到的同行文案）
      const mime = (file.raw && file.raw.type) || ''
      if (/^(video|audio)\//.test(mime)) {
        this.$message.info('🧠 已自动开始识别同行视频口播，稍后自动填入「同行文案」文本框（长视频可能几分钟）…')
        setTimeout(() => this.runPeerAsr(), 500)
      }
    },
    // 🧠 批次 23：同行视频自动识别口播 → 回填 form.source_text
    // 逻辑复用 runOwnAsr：分片上传 → 提交 ASR 任务 → 3 秒轮询（25 分钟上限）
    // 区别：只回填 form.source_text（口播原文），不生成五件套（五件套由后续「生成五件套」按钮走洗稿）
    async runPeerAsr() {
      if (!this._file) { this.$message.warning('请先选择同行视频/音频'); return }
      this.peerAsrLoading = true
      this.peerAsrStage = '正在上传素材到服务器…'
      this.peerAsrProgress = 0.02
      this.peerAsrElapsed = 0
      this._peerAsrStart = Date.now()
      if (this._peerAsrClock) clearInterval(this._peerAsrClock)
      this._peerAsrClock = setInterval(() => {
        this.peerAsrElapsed = Math.round((Date.now() - this._peerAsrStart) / 1000)
      }, 1000)
      try {
        // 🧠 批次 24：AI 重新识别不重新上传 —— 已识别过的素材复用 peerAsset.file_path
        let up = this.peerAsset
        if (!up || !up.file_path) {
          // 1) 分片上传（复用 uploadChunked，256KB 片 + 4 并发 + 断点续传）
          up = await uploadChunked(this._file, {
            account_id: localStorage.getItem('current_account_id') || 0,
            onProgress: (phase, pct, sizeTxt, dn, tt, extra) => {
              this.peerAsrProgress = 0.05 + 0.15 * pct
              if (phase === 'merge') this.peerAsrStage = '正在合并素材…'
              else if (phase === 'resume') this.peerAsrStage = `📎 从上次 ${Math.round((tt - dn) / tt * 0) || 0} 片继续上传…`
              else if (extra && extra.retry) this.peerAsrStage = `⚠️ 网络波动，第 ${extra.retry.idx}/${tt} 片重试 ${extra.retry.n}/${extra.retry.max}…`
              else this.peerAsrStage = `正在上传素材… ${Math.round(pct * 100)}% (${sizeTxt})`
            }
          })
          if (!up || !up.file_path) { this.$message.error('素材上传失败，请重试'); return }
          if (up.type === 'image') { this.$message.warning('图片素材没有语音，请上传视频后再识别'); return }
        } else {
          this.peerAsrStage = '📎 已上传过同一素材，跳过重新上传，让 AI 换个思路再识别一次…'
          this.peerAsrProgress = 0.15
        }
        this.peerAsset = { id: up.id, file_path: up.file_path, type: up.type }
        // 2) 提交 ASR 任务（后端毫秒级返回 task_id）
        this.peerAsrStage = '正在提交识别任务…'
        this.peerAsrProgress = 0.2
        const sub = await api.post('/content/video-asr', {
          file_path: up.file_path,
          industry: this.form.industry || '',
          brand: this.form.brand || '',
          platform: this.form.platform || 'douyin'
        }, { timeout: 60000 })
        const taskId = sub && sub.task_id
        if (!taskId) throw new Error('提交识别任务失败，请重试')

        // 3) 轮询拿结果
        const r = await this.pollAsrTaskPeer(taskId)
        const transcript = (r.transcript || '').trim()
        if (!transcript) {
          this.$message.warning('未识别到口播内容（可能是无声视频或纯图片），请改用「粘贴同行文案」直接填写')
        } else {
          // 只有 source_text 是空的或长度远小于识别结果时才回填（避免覆盖用户手写的文案）
          if (!this.form.source_text || this.form.source_text.length < transcript.length * 0.3) {
            this.form.source_text = transcript
            this.$message.success('✅ 识别完成：同行口播已填入「同行文案」文本框，可直接修改或点「生成五件套」洗稿')
          } else {
            this.$message.info('已识别同行口播 ' + transcript.length + ' 字（未覆盖你手写的文案）')
          }
        }
      } catch (e) {
        this.$message.error('AI 识别失败：' + (e.message || e))
      } finally {
        this.peerAsrLoading = false
        if (this._peerAsrClock) { clearInterval(this._peerAsrClock); this._peerAsrClock = null }
      }
    },
    // 批次 23：轮询同行 ASR 任务（独立于 ownAsr 的进度字段，避免互扰）
    pollAsrTaskPeer(taskId) {
      const MAX_MS = 25 * 60 * 1000
      const self = this
      return new Promise((resolve, reject) => {
        const tick = async () => {
          self._peerAsrTick = null
          if (self._peerAsrStart && Date.now() - self._peerAsrStart > MAX_MS) {
            reject(new Error('识别时间超过 25 分钟，请换一个更短的视频重新识别'))
            return
          }
          try {
            const s = await api.get('/content/video-asr/' + taskId, { timeout: 30000 })
            if (s && s.stage) self.peerAsrStage = s.stage
            if (typeof s.progress === 'number') {
              self.peerAsrProgress = Math.max(self.peerAsrProgress, Math.min(0.98, s.progress))
            }
            if (s.status === 'done') { resolve(s); return }
            if (s.status === 'failed') { reject(new Error(s.error || '语音识别失败')); return }
            self._peerAsrTick = setTimeout(tick, 3000)
          } catch (e) {
            self._peerAsrTick = setTimeout(tick, 8000)
          }
        }
        tick()
      })
    },
    onOwnFileChange(file) {
      this.ownFileName = file.name
      this._ownFile = file.raw
      this.ownAsset = null      // 换素材后缓存的上传结果失效
      // 🧠 批次 24：本地预览（不上传服务器，纯浏览器播放）
      if (this._ownPreviewUrl) URL.revokeObjectURL(this._ownPreviewUrl)
      this._ownPreviewUrl = URL.createObjectURL(file.raw)
      this.ownPreviewSrc = this._ownPreviewUrl
      this.ownPreviewIsVideo = (file.raw.type || '').startsWith('video/')
      this.ownAsr = null
      this.ownAsset = null
      this._clearCfAsrDraft()   // 五件套属于旧视频，清掉自动保存的识别草稿（手写文案保留）
      // 🧠 2026-09-06（批次 17）：上传视频/音频后自动开始识别，不用手动操作
      //    —— 语音转文字显示到文本框 → AI 提取标题描述与五件套 → 自动回填表单
      const mime = (file.raw && file.raw.type) || ''
      if (/^(video|audio)\//.test(mime)) {
        this.$message.info('🧠 已自动开始识别视频语音，稍后自动填好标题描述与五件套…')
        setTimeout(() => this.runOwnAsr(), 500)
      }
    },
    // 🧠 批次 25：「换」按钮触发隐藏 file input（同行素材）
    onPeerFileRef(e) {
      const f = e.target.files && e.target.files[0]
      if (!f) return
      this.onFileChange({ name: f.name, raw: f, type: f.type, size: f.size })
      e.target.value = ''
    },
    // 🧠 批次 25：「换」按钮触发隐藏 file input（自有素材）
    onOwnFileRef(e) {
      const f = e.target.files && e.target.files[0]
      if (!f) return
      this.onOwnFileChange({ name: f.name, raw: f, type: f.type, size: f.size })
      e.target.value = ''
    },
    // 🧠 批次 25：删除同行素材
    clearPeerFile() {
      this.$confirm('删除当前同行素材？识别到的口播文案也会一起清空。', '删除确认', {
        type: 'warning', confirmButtonText: '确认删除', cancelButtonText: '取消'
      }).then(() => {
        this.fileName = ''
        this._file = null
        this.peerAsset = null
        if (this._peerPreviewUrl) { URL.revokeObjectURL(this._peerPreviewUrl); this._peerPreviewUrl = null }
        this.peerPreviewSrc = ''
        this.peerPreviewIsVideo = false
        this.peerPreviewFullscreen = false
        this.form.source_text = ''
        this.$message.success('已删除同行素材，可重新上传')
      }).catch(() => {})
    },
    // 🧠 批次 25：删除自有素材
    clearOwnFile() {
      this.$confirm('删除当前自有素材？识别结果和五件套也会一起清空。', '删除确认', {
        type: 'warning', confirmButtonText: '确认删除', cancelButtonText: '取消'
      }).then(() => {
        this.ownFileName = ''
        this._ownFile = null
        this.ownAsset = null
        if (this._ownPreviewUrl) { URL.revokeObjectURL(this._ownPreviewUrl); this._ownPreviewUrl = null }
        this.ownPreviewSrc = ''
        this.ownPreviewIsVideo = false
        this.ownPreviewFullscreen = false
        this.ownAsr = null
        this._clearCfAsrDraft()
        this.$message.success('已删除自有素材，可重新上传')
      }).catch(() => {})
    },
    // 上传自有素材到素材库（识别与入队共用，返回 { id, file_path, type, size }）
    // 批次 19：改分片上传（1MB 一片 + 3 并发）—— 整包上传会被 Cloudflare 100/120s 读超时
    //          中途掐断（进度条跑一半就消失，且请求根本到不了后端）
    async uploadOwnAsset(showProgress) {
      return await uploadChunked(this._ownFile, {
        account_id: this.ownForm.account_id,
        onProgress: showProgress ? (phase, pct, sizeTxt, dn, tt, extra) => {
          this.ownAsrProgress = 0.05 + 0.15 * pct
          if (phase === 'merge') {
            this.ownAsrStage = '素材已上传完成，正在合并…'
          } else if (phase === 'resume') {
            this.ownAsrStage = '📎 检测到上次未完成上传，从 ' + Math.round(pct * 100) + '% 继续…'
          } else if (extra && extra.retry) {
            this.ownAsrStage = '⚠️ 网络波动，第 ' + dn + '/' + tt + ' 片重试中 ' + extra.retry + '/' + extra.maxRetry + '…（进度 ' + Math.round(pct * 100) + '%）'
          } else {
            this.ownAsrStage = '正在上传素材… ' + Math.round(pct * 100) + '%' + (extra && extra.resumed ? '（续传中）' : '')
          }
        } : null
      })
    },
    // 🧠 视频语音识别 → 转写原文 + 五件套（默认发布抖音）
    // 批次 18：改异步任务 + 轮询，彻底避开 Cloudflare 120 秒超时
    async runOwnAsr() {
      if (!this._ownFile) { this.$message.warning('请先选择素材文件'); return }
      this.ownAsrLoading = true
      this.ownAsrStage = '正在上传素材到服务器…'
      this.ownAsrProgress = 0.02
      this.ownAsrElapsed = 0
      this._ownAsrStart = Date.now()
      if (this._ownAsrClock) clearInterval(this._ownAsrClock)
      if (this._ownAsrTick) clearTimeout(this._ownAsrTick)
      this._ownAsrClock = setInterval(() => {
        this.ownAsrElapsed = Math.round((Date.now() - this._ownAsrStart) / 1000)
      }, 1000)
      try {
        // 🧠 批次 24：AI 重新识别不重新上传 —— 已识别过的素材复用 ownAsset.file_path
        let up = this.ownAsset
        if (!up || !up.file_path) {
          up = await this.uploadOwnAsset(true)
          if (!up || !up.file_path) {
            this.$message.error('素材上传失败，请重试')
            return
          }
          if (up.type === 'image') {
            this.$message.warning('图片素材没有语音，请上传视频后再识别')
            return
          }
        } else {
          this.ownAsrStage = '📎 已上传过同一素材，跳过重新上传，让 AI 换个思路再识别一次…'
          this.ownAsrProgress = 0.15
        }
        this.ownAsset = { id: up.id, file_path: up.file_path, type: up.type }
        // 提交识别任务（毫秒级返回 task_id）
        this.ownAsrStage = '正在提交识别任务…'
        this.ownAsrProgress = 0.2
        const sub = await api.post('/content/video-asr', {
          file_path: up.file_path,
          industry: this.form.industry || '',
          brand: this.form.brand || '',
          platform: this.ownForm.platform || 'douyin'
        }, { timeout: 60000 })
        const taskId = sub && sub.task_id
        if (!taskId) throw new Error('提交识别任务失败，请重试')
        const r = await this.pollAsrTask(taskId, 'own')
        this.ownAsr = r
        this.fillOwnFromAsr(true)
        this.$message.success('✅ 识别完成：标题描述与五件套已自动生成')
      } catch (e) {
        this.$message.error('AI 识别失败：' + (e.message || e))
      } finally {
        this.ownAsrLoading = false
        if (this._ownAsrClock) { clearInterval(this._ownAsrClock); this._ownAsrClock = null }
      }
    },
    // 🧠 批次 18：轮询异步识别任务；prefix='own' 时更新内容工厂的状态字段
    pollAsrTask(taskId, prefix) {
      const MAX_MS = 25 * 60 * 1000
      const self = this
      return new Promise((resolve, reject) => {
        const tick = async () => {
          self._ownAsrTick = null
          const start = prefix === 'own' ? self._ownAsrStart : (Date.now() - 0)
          if (start && Date.now() - start > MAX_MS) {
            reject(new Error('识别时间超过 25 分钟，请换一个更短的视频重新识别'))
            return
          }
          try {
            const s = await api.get('/content/video-asr/' + taskId, { timeout: 30000 })
            if (s && s.stage) self.ownAsrStage = s.stage
            if (typeof s.progress === 'number') {
              self.ownAsrProgress = Math.max(self.ownAsrProgress, Math.min(0.98, s.progress))
            }
            if (s.status === 'done') { resolve(s); return }
            if (s.status === 'failed') { reject(new Error(s.error || '语音识别失败')); return }
            self._ownAsrTick = setTimeout(tick, 3000)
          } catch (e) {
            self._ownAsrTick = setTimeout(tick, 8000)
          }
        }
        tick()
      })
    },
    // 把识别出的标题/描述回填到上方表单（silent=true 不弹提示）
    fillOwnFromAsr(silent) {
      const f = (this.ownAsr && this.ownAsr.five) || {}
      const title = (f.title || '').trim()
      const desc = (f.desc || '').trim()
      const tags = (f.title || '').match(/#[^\s#]+/g)
      const tagsLine = tags && tags.length ? '\n' + tags.join(' ') : ''
      this.ownForm.title = title.slice(0, 50)
      this.ownForm.desc = [desc, tagsLine].join('\n').trim()
      if (!this.ownForm.tags) this.ownForm.tags = (this.form.industry || '').trim()
      if (!silent) this.$message.success('已用识别结果回填标题+描述（默认发布抖音）')
    },
    // 五件套一键复制
    copyOwnAsrAll() {
      const f = (this.ownAsr && this.ownAsr.five) || {}
      const text = [
        '🎙 视频语音转文字：\n' + (this.ownAsr.transcript || ''),
        '',
        '① 抖音标题：' + (f.title || ''),
        '② 抖音描述：' + (f.desc || ''),
        '③ 评论区互动话术：' + (f.comment || ''),
        '④ 朋友圈文案：' + (f.moments || ''),
        '⑤ 小红书笔记：' + (f.xhs || '')
      ].join('\n\n')
      navigator.clipboard?.writeText(text).then(() => this.$message.success('已复制五件套'))
        .catch(() => this.$message.warning('复制失败，请手动选择复制'))
    },
    async uploadFile(file) {
      const fd = new FormData()
      fd.append('file', file)
      fd.append('account_id', this.form.account_id)
      const r = await api.post('/assets', fd, { timeout: 120000 })
      return r.id
    },
    async submit() {
      if (this.form.mode === 'rewrite' && !this.form.source_text.trim() && !this._file) {
        this.$message.warning('请粘贴同行文案或上传视频/音频')
        return
      }
      if (this.form.mode === 'generate' && !this.form.industry && !this.form.product_info.trim()) {
        this.$message.warning('AI 代写请至少填写行业或产品信息')
        return
      }
      this.submitting = true
      try {
        // 批次 23：只有「source_text 为空且选了文件」才走 uploadFile+asset_id 分支
        //          自动识别后 source_text 已填好 → 不再重复上传（否则大文件要传两遍）
        if (this.form.mode === 'rewrite' && !this.form.source_text.trim() && this._file) {
          this.form.asset_id = await this.uploadFile(this._file)
        }
        const body = { ...this.form }
        if (body.asset_id) body.source_text = ''
        const r = await api.post('/content/copywrite', body)
        this.taskId = r.task_id
        this.taskStatus = r.status
        this.$message.success(`任务 #${r.task_id} 已提交，正在生成…`)
        this.pollTask()
      } catch (e) {
        this.$message.error('提交失败：' + e.message)
      } finally {
        this.submitting = false
      }
    },
    async pollTask() {
      if (!this.taskId) return
      try {
        const r = await api.get(`/content/tasks/${this.taskId}`)
        this.taskStatus = r.status
        if (r.status === 'done') {
          const five = (r.result?.five_sets) || {}
          const keys = [
            ['title', '① 视频标题', 30, 3],
            ['desc', '② 视频文案', 200, 5],
            ['comment', '③ 评论区话术', 80, 3],
            ['moments', '④ 朋友圈文案', 120, 4],
            ['xhs', '⑤ 小红书笔记', 300, 6]
          ]
          this.five = five
          this.fiveList = keys.map(([k, label, max, rows]) => ({ key: k, label, max, rows, value: five[k] || '' }))
          const all = JSON.stringify(five)
          this.bannedHits = ['包治', '100%', '百分百', '根治', '永不复发', '最有效', '第一名', '国家级'].filter(w => all.includes(w))
          this.$message.success('五件套生成完成！')
        } else if (r.status === 'failed') {
          this.$message.error('生成失败：' + (r.error || '').slice(0, 200))
        } else {
          setTimeout(this.pollTask, 3000)
        }
      } catch (e) {
        this.$message.error('查询失败：' + e.message)
      }
    },
    async publishFive() {
      // 把五件套入发布队列（标题+文案，可后续在队列里补素材）
      try {
        const f = {}
        this.fiveList.forEach(i => { f[i.key] = i.value })
        await api.post('/publish', {
          account_id: this.form.account_id,
          platform: this.form.platform,
          media_type: 'text',
          file_path: '',
          title: f.title,
          desc: f.desc,
          tags: this.form.industry,
          mode: 'semi'
        })
        this.$message.success('已入发布队列')
        this.goPublish()
      } catch (e) {
        this.$message.error(e.message)
      }
    },
    // ═══════════ 🧠 批次 22：文案自动保存（localStorage）═══════════
    // 用户痛点：复制五件套要来回切菜单，切过去文本就没了。现在改一个字自动落盘，
    // 切走菜单、刷新、甚至关浏览器再打开，标题描述和五件套都还在，可直接复制。
    _cfDraftData() {
      return {
        form: {
          title: this.ownForm.title || '',
          desc: this.ownForm.desc || '',
          tags: this.ownForm.tags || ''
        },
        asr: this.ownAsr ? {
          transcript: this.ownAsr.transcript || '',
          transcript_len: this.ownAsr.transcript_len,
          asr_seconds: this.ownAsr.asr_seconds,
          warn: this.ownAsr.warn || '',
          five: this.ownAsr.five || {}
        } : null
      }
    },
    // 立即同步写入
    _saveCfDraftNow() {
      try {
        localStorage.setItem('zk_cf_draft_v1', JSON.stringify(this._cfDraftData()))
        this.cfSavedAt = new Date().toLocaleTimeString('zh-CN', { hour12: false })
      } catch (e) { /* localStorage 满 / 隐私模式，忽略不阻塞 */ }
    },
    // 500ms 防抖写入
    _saveCfDraft() {
      if (this._cfSaveTimer) clearTimeout(this._cfSaveTimer)
      this._cfSaveTimer = setTimeout(() => { this._cfSaveTimer = null; this._saveCfDraftNow() }, 500)
    },
    // 进页面恢复草稿
    _restoreCfDraft() {
      try {
        const raw = localStorage.getItem('zk_cf_draft_v1')
        if (!raw) return
        const d = JSON.parse(raw)
        if (!d) return
        const f = d.form || {}
        if (f.title) this.ownForm.title = f.title
        if (f.desc) this.ownForm.desc = f.desc
        if (f.tags) this.ownForm.tags = f.tags
        if (d.asr && d.asr.five) {
          d.asr.five = Object.assign({}, d.asr.five)
          this.ownAsr = d.asr
        }
        if (f.title || f.desc || d.asr) this.$message.info('📥 已恢复上次未发布的文案与五件套，切菜单也不会丢')
      } catch (e) { }
    },
    // 换素材后只清识别结果（五件套属于旧视频），保留手写的标题描述
    _clearCfAsrDraft() {
      try {
        const raw = localStorage.getItem('zk_cf_draft_v1')
        if (!raw) return
        const d = JSON.parse(raw)
        if (d) localStorage.setItem('zk_cf_draft_v1', JSON.stringify(Object.assign({}, d, { asr: null })))
      } catch (e) { }
      this.cfSavedAt = ''
    },

    // 2026-09-05：把五件套的标题/描述/标签填进「上传自有素材快速发布」表单（默认抖音）
    fillOwnFromFive() {
      const f = this.five || {}
      this.ownForm.platform = 'douyin'
      this.ownForm.title = (f.title || '').trim()
      this.ownForm.desc = (f.desc || '').trim()
      if (!this.ownForm.tags) this.ownForm.tags = (this.form.industry || '').trim()
      this.$message.success('已填入「上传自有素材快速发布」表单（平台默认抖音）——选素材后点「直接入发布队列」')
    },
    async submitOwn() {
      if (!this.ownForm.title.trim()) {
        this.$message.warning('请填写标题')
        return
      }
      this.ownSubmitting = true
      try {
        // 🧠 批次 25：无素材时允许纯文案入队（走 publishFive 相同的 text 分支）
        // 用户可能只想先在队列里存好文案，稍后补素材
        if (!this._ownFile && !this.ownAsset) {
          await api.post('/publish', {
            account_id: this.ownForm.account_id,
            platform: this.ownForm.platform,
            media_type: 'text',
            file_path: '',
            title: this.ownForm.title,
            desc: this.ownForm.desc,
            tags: this.ownForm.tags,
            mode: 'semi'
          })
          this.$message.success('文案已入发布队列 ✅（无素材，可在队列里补素材）')
          this.goPublish()
          return
        }
        // 识别环节已上传过同一文件 → 直接复用，避免大视频重复上传
        const r = await api.get('/assets?limit=100')
        const pool = r.assets || []
        let assetId = this.ownAsset && this.ownAsset.id
        let asset = assetId ? pool.find(a => a.id === assetId) : null
        if (!asset) {
          assetId = await this.uploadFile(this._ownFile)
          const r2 = await api.get('/assets?limit=100')
          asset = (r2.assets || []).find(a => a.id === assetId) || null
        }
        if (!asset) {
          this.$message.error('素材未找到，请重新选择文件')
          return
        }
        await api.post('/publish', {
          account_id: this.ownForm.account_id,
          platform: this.ownForm.platform,
          media_type: asset.type || 'video',
          file_path: asset.file_path,
          title: this.ownForm.title,
          desc: this.ownForm.desc,
          tags: this.ownForm.tags,
          mode: 'semi'
        })
        this.$message.success('素材+文案已入发布队列 ✅')
        this.goPublish()
      } catch (e) {
        this.$message.error('发布失败：' + e.message)
      } finally {
        this.ownSubmitting = false
      }
    },

    // 🧠 批次 25：立即发布（auto 模式，直接走 MuMu 执行，完成后状态变 done 自动进入「已发布」）
    async publishNow() {
      if (!this.ownForm.title.trim()) {
        this.$message.warning('请填写标题')
        return
      }
      const confirmed = await this.$confirm(
        '将立即通过 MuMu 模拟器里的抖音账号执行发布，约 90 秒完成。\n\n' +
        '请确认模拟器已登录您要发布的那个抖音号，否则会发错账号！',
        '🚀 立即发布确认', { type: 'warning', confirmButtonText: '确认立即发布', cancelButtonText: '再想想' }
      ).catch(() => null)
      if (!confirmed) return
      this.ownSubmitting = true
      try {
        let body = {
          account_id: this.ownForm.account_id,
          platform: this.ownForm.platform,
          title: this.ownForm.title,
          desc: this.ownForm.desc,
          tags: this.ownForm.tags,
          mode: 'auto'
        }
        // 有素材 → 附上文件；无素材 → 纯文案 auto 发布（媒体类型 text）
        if (this._ownFile || this.ownAsset) {
          let assetId = this.ownAsset && this.ownAsset.id
          let asset = null
          if (assetId) {
            const r = await api.get('/assets?limit=100')
            asset = (r.assets || []).find(a => a.id === assetId)
          }
          if (!asset) {
            assetId = await this.uploadFile(this._ownFile)
            const r2 = await api.get('/assets?limit=100')
            asset = (r2.assets || []).find(a => a.id === assetId) || null
          }
          if (!asset) {
            this.$message.error('素材未找到，请重新选择文件')
            return
          }
          body.media_type = asset.type || 'video'
          body.file_path = asset.file_path
        } else {
          body.media_type = 'text'
          body.file_path = ''
        }
        const res = await api.post('/publish', body)
        const pid = res.id || res.publish_id
        if (!pid) {
          this.$message.success('已入发布队列（未拿到 ID，无法立即执行）')
          this.goPublish()
          return
        }
        // 触发立即执行
        await api.post(`/publish/${pid}/execute`)
        this.$message.success('🚀 已开始立即发布，约 90 秒后完成')
        this.goPublish()
      } catch (e) {
        this.$message.error('立即发布失败：' + e.message)
      } finally {
        this.ownSubmitting = false
      }
    },

    // 🧠 批次 25：AI 智能重写（点击一次生成一次不同文案，覆盖当前文本框）
    async aiRewrite(field, currentVal, role) {
      const v = (currentVal || '').trim()
      if (!v) {
        this.$message.warning('当前文本框为空，请先写点内容再点 AI 重写')
        return
      }
      this._rwBusy = field
      try {
        const r = await api.post('/assistant/rewrite', {
          text: v,
          role: role || 'desc',
          industry: this.form.industry || (this.ownForm && this.ownForm.tags) || '',
          seed: (Date.now() % 1000)
        }, { timeout: 90000 })
        if (!r.ok || !r.text) {
          this.$message.warning('AI 暂无返回，请稍后重试')
          return
        }
        // field 形如 "ownForm.title" 或 "ownAsr.five.comment" → 拆成路径
        const parts = field.split('.')
        let obj = this
        for (let i = 0; i < parts.length - 1; i++) obj = obj[parts[i]]
        const key = parts[parts.length - 1]
        obj[key] = r.text
        this.$message.success(`✨ AI 已重写（${r.style || ''}风格）`)
      } catch (e) {
        this.$message.error('AI 重写失败：' + e.message)
      } finally {
        this._rwBusy = null
      }
    },
    // 一键重生 5 件套：依次调用 AI 重写（并发 5 个请求），失败静默降级
    async regenerateFive() {
      if (!this.ownAsr || !this.ownAsr.five) {
        this.$message.warning('尚无五件套可重生，请先上传视频并让 AI 识别')
        return
      }
      this.fiveRegenLoading = true
      try {
        const f = this.ownAsr.five
        const ind = this.form.industry || (this.ownForm && this.ownForm.tags) || ''
        const jobs = [
          ['title', 'title', f.title],
          ['desc', 'desc', f.desc],
          ['comment', 'comment', f.comment],
          ['moments', 'moments', f.moments],
          ['xhs', 'xhs', f.xhs]
        ].filter(x => x[2])
        await Promise.all(jobs.map(async ([key, role, cur]) => {
          try {
            const r = await api.post('/assistant/rewrite', {
              text: cur, role, industry: ind, seed: (Date.now() + key.length) % 1000
            }, { timeout: 90000 })
            if (r.ok && r.text) f[key] = r.text
          } catch (e) { /* 单条失败静默 */ }
        }))
        this.$message.success('🎨 五件套已全部重生')
      } finally {
        this.fiveRegenLoading = false
      }
    },
    copyAll() {
      const text = this.fiveList.map(i => i.label.replace(/^[①②③④⑤]\s*/, '') + '：\n' + i.value).join('\n\n')
      navigator.clipboard?.writeText(text).then(() => this.$message.success('已复制全部'))
        .catch(() => this.$message.warning('复制失败，请手动选择复制'))
    },
    goPublish() {
      this.$router.push('/publish')
    }
  }
}
</script>

<style scoped>
.content-factory { padding: 4px; }
.card-header { display: flex; justify-content: space-between; align-items: center; }
.mt-16 { margin-top: 16px; }
.upload-row { display: flex; align-items: center; gap: 12px; }
.file-name { color: #888; font-size: 13px; }
.hint { color: #999; font-size: 12px; margin-left: 8px; }
/* 🧠 批次 25：AI 智能重写小图标按钮 —— 与文本框同一行右侧，紧凑不压版面 */
.ai-rewrite-btn { height: 40px; padding: 0 12px; flex-shrink: 0; background: linear-gradient(135deg, rgba(139, 92, 246, .18), rgba(59, 130, 246, .20)); border: 1px solid rgba(139, 92, 246, .40); color: #c4b5fd; }
.ai-rewrite-btn:hover:not(.is-disabled) { background: linear-gradient(135deg, rgba(139, 92, 246, .30), rgba(59, 130, 246, .32)); color: #e9d5ff; border-color: rgba(139, 92, 246, .60); }
/* 批次 21：识别区操作栏（回填/复制/重新识别），深色主题半透明蓝底 */
.asr-ops { display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 14px; padding: 10px 12px; border-radius: 10px; background: rgba(37, 99, 235, .12); border: 1px solid rgba(37, 99, 235, .30); }
.five-item { border: 1px solid var(--el-border-color-light, #ebeef5); border-radius: 8px; padding: 10px; background: var(--el-fill-color, #fafafa); }
.five-label { font-weight: 600; margin-bottom: 8px; color: var(--el-text-color-primary, #303133); }

/* 🧠 批次 20：识别进行中进度条 —— 深色主题适配（半透明蓝底，不用白色色块） */
.asr-progress {
  margin-bottom: 14px;
  padding: 10px 14px;
  border-radius: 8px;
  background: linear-gradient(90deg, rgba(37, 99, 235, .14), rgba(34, 211, 238, .10));
  border: 1px solid rgba(37, 99, 235, .38);
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.asr-progress .sp { font-size: 14px; font-weight: 600; color: #93c5fd; }
.asr-progress .sub { font-size: 12px; color: #60a5fa; opacity: .85; }
.asr-progress-run {
  background: linear-gradient(135deg, rgba(37, 99, 235, .16) 0%, rgba(30, 64, 175, .24) 100%);
  border: 1px solid rgba(37, 99, 235, .45);
  padding: 10px 14px;
  gap: 7px;
  animation: asrRunIn2 .3s ease;
}
@keyframes asrRunIn2 { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
.asr-pg-row { display: flex; align-items: center; gap: 12px; }
.asr-pg-spin {
  width: 26px; height: 26px; flex-shrink: 0;
  border-radius: 50%;
  border: 3px solid rgba(148, 163, 184, .28);
  border-top-color: #3b82f6;
  animation: asrSpin2 .9s linear infinite;
}
@keyframes asrSpin2 { to { transform: rotate(360deg); } }
.asr-pg-main { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
.asr-pg-main .stg { font-size: 13px; color: #bfdbfe; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.asr-pg-main .el { font-size: 13px; font-weight: 600; color: #60a5fa; white-space: nowrap; }
.asr-pg-bar { height: 7px; background: rgba(148, 163, 184, .22); border-radius: 4px; overflow: hidden; }
.asr-pg-bar-inner {
  height: 100%; border-radius: 4px;
  background: linear-gradient(90deg, #2563eb, #3b82f6, #22d3ee, #2563eb);
  background-size: 200% 100%;
  transition: width .6s ease;
  animation: asrBarFlow2 2.4s linear infinite;
}
@keyframes asrBarFlow2 { 0% { background-position: 0% 0; } 100% { background-position: -200% 0; } }
.asr-divider {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  font-size: 15px;
  font-weight: 700;
}
.asr-meta { font-size: 12px; font-weight: 400; color: #8a94a6; }
/* 🧠 批次 22：自动保存状态提示（文案切菜单不丢） */
.save-tip { font-size: 12px; font-weight: 400; color: #67c23a; }
@media (max-width: 767px) {
  .card-header { flex-wrap: wrap; gap: 6px; }
  .upload-row { flex-wrap: wrap; }
}
/* 🧠 批次 25：上传成功后紧凑文件条（不再显示大文件框，只有缩略图+状态+4 操作） */
.file-compact {
  display: flex; align-items: center; gap: 10px;
  margin: 4px 0 10px; padding: 10px 12px;
  background: linear-gradient(135deg, rgba(37,99,235,.10), rgba(34,211,238,.08));
  border: 1px solid rgba(37,99,235,.32); border-radius: 10px;
}
.file-compact .thumb-wrap {
  flex-shrink: 0; width: 72px; height: 48px;
  border-radius: 6px; overflow: hidden;
  background: #000; cursor: pointer; position: relative;
  display: flex; align-items: center; justify-content: center;
}
.file-compact .thumb-wrap:hover { outline: 2px solid #2563eb; }
.file-compact .thumb { width: 100%; height: 100%; object-fit: cover; }
.file-compact .thumb-play {
  position: absolute; inset: 0; display: flex; align-items: center; justify-content: center;
  color: #fff; font-size: 18px; text-shadow: 0 1px 3px rgba(0,0,0,.6);
  background: linear-gradient(180deg, transparent 60%, rgba(0,0,0,.55));
}
.file-compact .thumb-empty { color: #64748b; font-size: 22px; }
.file-compact .file-info { flex: 1; min-width: 0; }
.file-compact .file-status { font-size: 13px; font-weight: 600; color: #67c23a; line-height: 1.3; }
.file-compact .file-name-sm {
  font-size: 12px; color: #94a3b8;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  margin-top: 2px;
}
.file-compact .file-actions { display: flex; gap: 4px; flex-shrink: 0; }
.file-compact .file-actions .el-button { padding: 6px 10px; }
@media (max-width: 767px) {
  .file-compact { flex-wrap: wrap; }
  .file-compact .file-actions { flex: 1; }
  .file-compact .file-actions .el-button { flex: 1; padding: 6px 4px; }
}
/* 🧠 批次 24：上传后本地预览 + 点击放大（不影响上传链路，纯 UI 增强） */
.media-preview {
  position: relative; margin: 10px 0; padding: 6px;
  border: 1px dashed rgba(37,99,235,.35); border-radius: 8px;
  background: rgba(37,99,235,.06); cursor: pointer;
  transition: all .2s;
}
.media-preview:hover { border-color: rgba(37,99,235,.7); background: rgba(37,99,235,.12); }
.preview-video {
  display: block; width: 100%; max-height: 240px; border-radius: 6px;
  background: #000; object-fit: contain;
}
.preview-img {
  display: block; max-width: 100%; max-height: 240px; border-radius: 6px;
  object-fit: contain;
}
.preview-hint {
  position: absolute; top: 12px; right: 12px;
  padding: 4px 10px; font-size: 12px; color: #fff;
  background: rgba(0,0,0,.55); border-radius: 4px;
  pointer-events: none;
}
.preview-modal {
  position: fixed; inset: 0; z-index: 3000;
  background: rgba(0,0,0,.88);
  display: flex; align-items: center; justify-content: center;
}
.preview-modal-inner {
  position: relative; max-width: 92vw; max-height: 92vh;
  display: flex; flex-direction: column; align-items: center; gap: 10px;
}
.preview-modal-media {
  max-width: 92vw; max-height: 82vh;
  border-radius: 8px; background: #000;
}
.preview-modal-actions .el-button {
  padding: 8px 22px; font-size: 14px;
}
</style>
